import { supabase } from "@/lib/supabase/client";
import { getMyProfileId } from "@/features/identity/api/profileApi";

const CARD_COLUMNS = "id,profile_id,name,slug,template_id,design_config,status,is_primary,created_at,updated_at";
const NIL_UUID = "00000000-0000-0000-0000-000000000000";

export async function getMyPrimaryCard(profileId) {
  const pid = profileId ?? (await getMyProfileId());
  const { data, error } = await supabase
    .from("digital_cards")
    .select(CARD_COLUMNS)
    .eq("profile_id", pid)
    .eq("is_primary", true)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// RLS hides other users' cards and no slug RPC exists, so availability is probed with a 2-row insert whose
// second row always fails (nil profile_id) — Postgres rolls back the whole statement, nothing is written.
export async function checkSlugAvailability(slug) {
  const pid = await getMyProfileId();
  const row = { profile_id: pid, name: "slug-check", slug, template_id: "card_01", status: "draft", is_primary: false, design_config: {} };
  const { error } = await supabase.from("digital_cards").insert([row, { ...row, profile_id: NIL_UUID }]);
  if (!error) return "unknown";
  if (error.code === "23505") return error.message.includes("slug") ? "taken" : "unknown";
  if (error.code === "42501" || error.code === "23503") return "available";
  throw error;
}

export async function findPublicProfileForCard(cardId) {
  const { data, error } = await supabase.from("public_profiles").select("id,slug,design_config,status,digital_card_id").eq("digital_card_id", cardId).maybeSingle();
  if (error) throw error;
  return data;
}

// Keeps public_profiles.slug equal to the card slug; reverts the profile slug if the card update fails.
export async function savePrimaryCard(draft) {
  const pid = await getMyProfileId();
  const payload = { name: draft.name.trim(), slug: draft.slug, template_id: draft.template_id, design_config: draft.design_config };
  const existing = await getMyPrimaryCard(pid);
  const pp = existing && existing.slug !== payload.slug ? await findPublicProfileForCard(existing.id) : null;
  if (pp) {
    const { error } = await supabase.from("public_profiles").update({ slug: payload.slug }).eq("id", pp.id);
    if (error) throw error;
  }
  const query = existing
    ? supabase.from("digital_cards").update(payload).eq("id", existing.id).eq("profile_id", pid)
    : supabase.from("digital_cards").insert({ ...payload, profile_id: pid, is_primary: true, status: "draft" });
  const { data, error } = await query.select(CARD_COLUMNS).single();
  if (error) {
    if (pp) await supabase.from("public_profiles").update({ slug: existing.slug }).eq("id", pp.id);
    throw error;
  }
  return data;
}
